import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { paymentAPI } from '@/lib/api';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, attempts]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      toast.warning('Payment verification timed out. Please check your dashboard.');
      return;
    }

    try {
      const response = await paymentAPI.getPaymentStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful! Credits added to your account.');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        toast.error('Payment session expired');
      } else {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
        }, 2000);
      }
    } catch (error) {
      setStatus('error');
      toast.error('Failed to verify payment status');
    }
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const dashboardPath = user.role === 'seller' ? '/seller/dashboard' : '/buyer/dashboard';

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 text-center">
        {status === 'checking' && (
          <>
            <div className="animate-pulse mb-4">
              <CheckCircle className="w-16 h-16 text-slate-400 mx-auto" />
            </div>
            <h1 className="text-2xl font-chivo font-bold text-slate-900 mb-2">
              Verifying Payment
            </h1>
            <p className="text-slate-600">
              Please wait while we confirm your payment...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h1 data-testid="success-heading" className="text-2xl font-chivo font-bold text-slate-900 mb-2">
              Payment Successful!
            </h1>
            <p className="text-slate-600 mb-6">
              Your credits have been added to your account.
            </p>
            <Button
              data-testid="goto-dashboard-btn"
              onClick={() => navigate(dashboardPath)}
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {(status === 'timeout' || status === 'error' || status === 'expired') && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⚠️</span>
            </div>
            <h1 className="text-2xl font-chivo font-bold text-slate-900 mb-2">
              {status === 'expired' ? 'Payment Expired' : 'Verification Issue'}
            </h1>
            <p className="text-slate-600 mb-6">
              {status === 'expired' 
                ? 'The payment session has expired. Please try again.' 
                : 'We\'re having trouble verifying your payment. Please check your dashboard.'}
            </p>
            <Button onClick={() => navigate(dashboardPath)}>
              Go to Dashboard
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

export default PaymentSuccess;
